import {
    ArrowLeftIcon,
    ArrowRightIcon,
    InformationCircleIcon,
    ListBulletIcon,
    NumberedListIcon,
    SparklesIcon,
} from '@heroicons/react/24/outline';
import { ActionIcon, Box, Button, Group, LoadingOverlay, Modal, Text, Textarea, TextareaProps, Tooltip, useMantineColorScheme } from '@mantine/core';
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { ChatService } from '../../services/chatService';

/**
 * FormattableTextArea - Plain text editor with formatting toolbar and ADR 126 normalization
 * 
 * Provides simple text formatting (bullets, numbers, indentation) with automatic normalization:
 * - Normalizes on mount: Cleans database content immediately when loaded
 * - Normalizes on paste: Cleans messy copied content with cursor preservation
 * - Normalizes on blur: Standardizes format when leaving field with cursor preservation
 * 
 * Uses ADR 126 normalization patterns (consistent with export):
 * - Varied bullets (-, *, ▪, ◼, o) → "•" (U+2022)
 * - Numbered lists: "1)" → "1."
 * - Tabs → 2 spaces
 * - Optional: Auto-format action verbs (Designed, Developed, etc.)
 * - Note: Multiple space collapse temporarily disabled to preserve indentation
 * 
 * Interactive Features:
 * - Smart list continuation: Press Enter to continue bullets/numbers
 * - Smart list exit: Press Enter on empty bullet to exit list mode
 * - Tab/Shift+Tab for indentation
 * - Toolbar buttons for formatting (maintain focus)
 * 
 * Cursor Position Preservation:
 * - All normalization operations preserve cursor position
 * - Calculates how text before cursor changes during normalization
 * - Adjusts cursor position by the delta to maintain editing context
 * 
 * Storage: Plain text (no HTML, no markdown)
 * Benefits: WYAWG (What You Author is What you Get) - consistent with document export
 * Natural migration: Legacy database content automatically cleaned up when viewed
 * 
 * @see doc/ADR/implemented/ADR_2026-02-20_02_FormattableTextArea_Component_For_Structured_Content.md
 * @see doc/ADR/implemented/ADR_126_text_normalization_for_document_generation.md
 * @see doc/Implementation-notes/ADR_2026-02-23_01_IN_FormattableTextArea_Critical_Bug_Fixes.md
 */

interface FormattableTextAreaProps extends Omit<TextareaProps, 'onChange'> {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    label?: string;
    required?: boolean;
    minRows?: number;
    maxRows?: number;
    disabled?: boolean;
    error?: string;
    /**
     * Enable ADR 126 normalization (automatic approach with cursor preservation)
     * - Normalizes on mount: Cleans database content when loaded
     * - Normalizes on paste: Cleans copied content with cursor preservation
     * - Normalizes on blur: Standardizes format when leaving field with cursor preservation
     * - Normalizes varied bullets (-, *, ▪, ◼, o) to "•"
     * - Normalizes numbered lists: "1)" to "1."
     * - Normalizes tabs to 2 spaces
     * - Optionally auto-formats action verbs (Designed, Developed, etc.)
     * - All normalization preserves cursor position by calculating text delta before cursor
     * @default true
     */
    enableNormalization?: boolean;
    /**
     * Auto-format action verbs as bullet points
     * (e.g., "Designed the architecture" → "• Designed the architecture")
     * Only applies when enableNormalization is true
     * @default false
     */
    autoFormatActionVerbs?: boolean;
    /**
     * Enable AI text improvement button in the toolbar
     * @default false
     */
    enableImprovement?: boolean;
    /**
     * Custom prompt for the AI improvement request
     * @default "Please improve this text while maintaining its core facts:"
     */
    improvePrompt?: string;
}

export function FormattableTextArea({
    value,
    onChange,
    onBlur,
    label,
    required = false,
    minRows = 5,
    maxRows = 20,
    disabled = false,
    error,
    enableNormalization = true,
    autoFormatActionVerbs = false,
    enableImprovement = false,
    improvePrompt = 'Please improve this text while maintaining its core facts:',
    ...textareaProps
}: FormattableTextAreaProps) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const hasNormalizedInitialValue = useRef(false);
    const lastNormalizedValue = useRef<string>('');
    const isNormalizingRef = useRef(false);
    const timeoutIdsRef = useRef<NodeJS.Timeout[]>([]);
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const [isImproveModalOpen, setIsImproveModalOpen] = useState(false);
    const [isImproving, setIsImproving] = useState(false);
    const [improvedText, setImprovedText] = useState('');

    // Get selection/cursor info: boundaries, text, line info
    const getSelectionInfo = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea) return null;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;

        // Find line boundaries
        const lineStart = text.lastIndexOf('\n', start - 1) + 1;
        const lineEnd = text.indexOf('\n', end);
        const actualLineEnd = lineEnd === -1 ? text.length : lineEnd;

        return {
            start,
            end,
            lineStart,
            lineEnd: actualLineEnd,
            hasSelection: start !== end,
            selectedText: text.substring(start, end),
            currentLine: text.substring(lineStart, actualLineEnd),
            beforeSelection: text.substring(0, start),
            afterSelection: text.substring(end),
        };
    }, []);

    // Normalize text: bullets (-, *, ▪, ◼, o → •), numbers (1) → 1.), tabs → spaces (ADR 126)
    const normalizeBullets = useCallback((text: string): string => {
        if (!text) return text;

        let normalized = text;

        // 0. Remove double/mixed bullets first (e.g., "• -", "• *", "- •", "* •", etc.)
        // This prevents creating double bullets when normalizing
        normalized = normalized.replace(/^(\s*)[•·∙⦁⦿]\s*[-*▪◼○●■□☐✓✔✗✘➔➢➤→⇒]\s+/gm, '$1• ');
        normalized = normalized.replace(/^(\s*)[-*▪◼○●■□☐✓✔✗✘➔➢➤→⇒]\s*[•·∙⦁⦿]\s+/gm, '$1• ');
        // Handle specific cases like "• •" or "- -"
        normalized = normalized.replace(/^(\s*)[•·∙⦁⦿]\s+[•·∙⦁⦿]\s+/gm, '$1• ');
        normalized = normalized.replace(/^(\s*)[-*]\s+[-*]\s+/gm, '$1• ');

        // 1. Normalize bullet characters (ADR 126)
        normalized = normalized.replace(/^(\s*)[-*▪◼○●■□☐✓✔✗✘➔➢➤→⇒]\s+/gm, '$1• ');
        normalized = normalized.replace(/^(\s*)[•·∙⦁⦿]\s+/gm, '$1• ');

        // 2. Handle "o" as bullet (but not "on", "of", etc.)
        normalized = normalized.replace(/^(\s*)o\s+(?=[A-Z])/gm, '$1• ');

        // 3. Normalize numbered lists: "1)" to "1."
        normalized = normalized.replace(/^(\s*)(\d+)\)\s+/gm, '$1$2. ');

        // 4. Normalize tabs to 2 spaces
        normalized = normalized.replace(/\t/g, '  ');

        // 5. Collapse multiple spaces/tabs ONLY (NOT newlines - user controls line breaks!)
        // TEMPORARILY DISABLED - Causes issues with leading space preservation
        // normalized = normalized.replace(/([^\n\r])[ \t]{2,}/g, '$1 ');

        // 6. Optional: Auto-format action verbs (ADR 126)
        if (autoFormatActionVerbs) {
            const actionVerbs = [
                'Designed', 'Developed', 'Implemented', 'Created', 'Built', 'Managed', 'Led',
                'Optimized', 'Integrated', 'Configured', 'Deployed', 'Maintained', 'Tested',
                'Analyzed', 'Coordinated', 'Established', 'Improved', 'Executed', 'Planned',
                'Architected', 'Migrated', 'Refactored', 'Documented', 'Collaborated'
            ];

            actionVerbs.forEach(verb => {
                // Only add bullet if line starts with action verb and doesn't already have one
                const regex = new RegExp(`^(\\s*)${verb}\\s+(?!•)`, 'gm');
                normalized = normalized.replace(regex, `$1• ${verb} `);
            });
        }

        return normalized;
    }, [autoFormatActionVerbs]);

    // Normalize initial value when data loads (natural migration for database data)
    // Resets normalization flag only when switching to completely different CV (not on user edits)
    useEffect(() => {
        // Reset flag only on genuine CV switch (not on user edits)
        if (value && lastNormalizedValue.current) {
            const oldVal = lastNormalizedValue.current;
            const newVal = value;

            // Skip check if old value was empty (initial mount)
            if (oldVal.trim() === '') {
                hasNormalizedInitialValue.current = false;
                lastNormalizedValue.current = value;
            } else {
                // Check if this is a user edit (new value contains old or vice versa)
                const isUserEdit = newVal.includes(oldVal) || oldVal.includes(newVal);

                if (!isUserEdit) {
                    // Completely different content - must be CV switch
                    hasNormalizedInitialValue.current = false;
                    lastNormalizedValue.current = value;
                }
            }
        }

        if (!hasNormalizedInitialValue.current && enableNormalization && value) {
            const normalized = normalizeBullets(value);
            if (normalized !== value) {
                // Mark as normalized BEFORE calling onChange to prevent re-normalization
                hasNormalizedInitialValue.current = true;
                lastNormalizedValue.current = normalized;

                // Use setTimeout to avoid React batch update issues
                const timeoutId = setTimeout(() => {
                    isNormalizingRef.current = true;
                    onChange(normalized);
                    isNormalizingRef.current = false;
                }, 0);
                timeoutIdsRef.current.push(timeoutId);
            } else {
                // No normalization needed, but mark as processed
                hasNormalizedInitialValue.current = true;
                lastNormalizedValue.current = value;
            }
        }

        // Cleanup function to cancel pending setTimeout calls
        return () => {
            timeoutIdsRef.current.forEach(clearTimeout);
            timeoutIdsRef.current = [];
        };
    }, [value, enableNormalization, normalizeBullets, onChange]);

    // Handle paste: normalize content, preserve cursor position
    const handlePaste = useCallback(
        (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
            if (!enableNormalization) return; // Skip normalization if disabled

            const pastedText = e.clipboardData.getData('text');
            const normalized = normalizeBullets(pastedText);

            if (normalized !== pastedText) {
                e.preventDefault();
                const textarea = textareaRef.current;
                if (!textarea) return;

                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const before = value.substring(0, start);
                const after = value.substring(end);

                const newValue = before + normalized + after;
                onChange(newValue);

                // Set cursor position after paste
                const timeoutId = setTimeout(() => {
                    const newPosition = start + normalized.length;
                    textarea.setSelectionRange(newPosition, newPosition);
                }, 0);
                timeoutIdsRef.current.push(timeoutId);
            }
        },
        [value, onChange, normalizeBullets, enableNormalization]
    );

    // Handle blur: normalize content, preserve cursor by calculating delta (normalizes twice: full text + text before cursor)
    const handleBlur = useCallback(() => {
        if (!isNormalizingRef.current && enableNormalization && value) {
            const textarea = textareaRef.current;
            if (!textarea) return;

            // Save cursor position before normalization
            const cursorStart = textarea.selectionStart;
            const cursorEnd = textarea.selectionEnd;

            const normalized = normalizeBullets(value);
            if (normalized !== value) {
                isNormalizingRef.current = true;

                // Calculate how text before cursor changes during normalization
                // We normalize text before cursor separately to get accurate delta
                const textBeforeCursor = value.substring(0, cursorStart);
                const normalizedBeforeCursor = normalizeBullets(textBeforeCursor);
                const cursorDelta = normalizedBeforeCursor.length - textBeforeCursor.length;

                onChange(normalized);

                // Restore cursor position after state updates
                const timeoutId = setTimeout(() => {
                    if (textareaRef.current) {
                        const newCursorStart = Math.max(0, Math.min(normalized.length, cursorStart + cursorDelta));
                        const newCursorEnd = cursorStart === cursorEnd
                            ? newCursorStart
                            : Math.max(0, Math.min(normalized.length, cursorEnd + cursorDelta));
                        textareaRef.current.setSelectionRange(newCursorStart, newCursorEnd);
                    }
                    isNormalizingRef.current = false;
                }, 0);
                timeoutIdsRef.current.push(timeoutId);
            }
        }
        onBlur?.(); // Call original onBlur if provided
    }, [value, onChange, normalizeBullets, enableNormalization, onBlur]);

    // Add/remove bullets: toggle on single line or all selected lines, preserve indentation + cursor
    const handleAddBullets = useCallback(() => {
        const info = getSelectionInfo();
        if (!info) return;

        const textarea = textareaRef.current;
        if (!textarea) return;

        const { beforeSelection, afterSelection, start, end } = info;
        const selectedText = value.substring(start, end);

        let newText: string;
        let newCursorPos: number;

        if (!selectedText.includes('\n')) {
            // Single line or cursor position
            const lines = value.split('\n');
            let currentPos = 0;
            let lineIndex = 0;

            for (let i = 0; i < lines.length; i++) {
                if (currentPos + lines[i].length >= start) {
                    lineIndex = i;
                    break;
                }
                currentPos += lines[i].length + 1; // +1 for \n
            }

            const line = lines[lineIndex];
            const trimmedLine = line.trimStart();
            const leadingSpaces = line.slice(0, line.length - trimmedLine.length);

            if (!trimmedLine.startsWith('• ')) {
                lines[lineIndex] = leadingSpaces + '• ' + trimmedLine;
                newText = lines.join('\n');
                newCursorPos = start + 2; // Added "• "
            } else {
                // Remove bullet
                lines[lineIndex] = leadingSpaces + trimmedLine.substring(2);
                newText = lines.join('\n');
                newCursorPos = Math.max(start - 2, 0);
            }
        } else {
            // Multiple lines selected
            const lines = selectedText.split('\n');
            const modifiedLines = lines.map((line) => {
                const trimmedLine = line.trimStart();
                const leadingSpaces = line.slice(0, line.length - trimmedLine.length);

                if (!trimmedLine.startsWith('• ')) {
                    return leadingSpaces + '• ' + trimmedLine;
                } else {
                    return leadingSpaces + trimmedLine.substring(2);
                }
            });

            newText = beforeSelection + modifiedLines.join('\n') + afterSelection;
            newCursorPos = end + modifiedLines.join('\n').length - selectedText.length;
        }

        onChange(newText);
        const timeoutId = setTimeout(() => {
            textarea.setSelectionRange(newCursorPos, newCursorPos);
            textarea.focus();
        }, 0);
        timeoutIdsRef.current.push(timeoutId);
    }, [value, onChange, getSelectionInfo]);

    // Add/remove numbering: toggle "1. " or sequential (1. 2. 3. ...), preserve indentation + cursor
    const handleAddNumbering = useCallback(() => {
        const info = getSelectionInfo();
        if (!info) return;

        const textarea = textareaRef.current;
        if (!textarea) return;

        const { beforeSelection, afterSelection, start, end } = info;
        const selectedText = value.substring(start, end);

        if (!selectedText.includes('\n')) {
            // Single line
            const lines = value.split('\n');
            let currentPos = 0;
            let lineIndex = 0;

            for (let i = 0; i < lines.length; i++) {
                if (currentPos + lines[i].length >= start) {
                    lineIndex = i;
                    break;
                }
                currentPos += lines[i].length + 1;
            }

            const line = lines[lineIndex];
            const trimmedLine = line.trimStart();
            const leadingSpaces = line.slice(0, line.length - trimmedLine.length);

            if (!/^\d+\.\s/.test(trimmedLine)) {
                lines[lineIndex] = leadingSpaces + '1. ' + trimmedLine;
                onChange(lines.join('\n'));
                const timeoutId1 = setTimeout(() => {
                    const newPos = start + 3;
                    textarea.setSelectionRange(newPos, newPos);
                    textarea.focus();
                }, 0);
                timeoutIdsRef.current.push(timeoutId1);
            } else {
                // Remove numbering
                const withoutNumber = trimmedLine.replace(/^\d+\.\s/, '');
                lines[lineIndex] = leadingSpaces + withoutNumber;
                onChange(lines.join('\n'));
                const timeoutId2 = setTimeout(() => {
                    const newPos = Math.max(start - 3, 0);
                    textarea.setSelectionRange(newPos, newPos);
                    textarea.focus();
                }, 0);
                timeoutIdsRef.current.push(timeoutId2);
            }
        } else {
            // Multiple lines
            const lines = selectedText.split('\n');
            const modifiedLines = lines.map((line, index) => {
                const trimmedLine = line.trimStart();
                const leadingSpaces = line.slice(0, line.length - trimmedLine.length);

                if (!/^\d+\.\s/.test(trimmedLine)) {
                    return leadingSpaces + `${index + 1}. ` + trimmedLine;
                } else {
                    return leadingSpaces + trimmedLine.replace(/^\d+\.\s/, '');
                }
            });

            const newText = beforeSelection + modifiedLines.join('\n') + afterSelection;
            onChange(newText);
            const timeoutId = setTimeout(() => {
                const newPos = end + modifiedLines.join('\n').length - selectedText.length;
                textarea.setSelectionRange(newPos, newPos);
                textarea.focus();
            }, 0);
            timeoutIdsRef.current.push(timeoutId);
        }
    }, [value, onChange, getSelectionInfo]);

    // Indent: add 2 spaces at line start, adjust cursor position
    const handleIndent = useCallback(() => {
        const info = getSelectionInfo();
        if (!info) return;

        const textarea = textareaRef.current;
        if (!textarea) return;

        const { start, end } = info;
        const selectedText = value.substring(start, end);
        const beforeSelection = value.substring(0, start);
        const afterSelection = value.substring(end);

        if (!selectedText.includes('\n')) {
            // Single line - add 2 spaces at line start
            const lines = value.split('\n');
            let currentPos = 0;
            let lineIndex = 0;

            for (let i = 0; i < lines.length; i++) {
                if (currentPos + lines[i].length >= start) {
                    lineIndex = i;
                    break;
                }
                currentPos += lines[i].length + 1;
            }

            lines[lineIndex] = '  ' + lines[lineIndex];
            const newText = lines.join('\n');
            onChange(newText);

            const timeoutId = setTimeout(() => {
                const newPos = start + 2;
                textarea.setSelectionRange(newPos, newPos);
                textarea.focus();
            }, 0);
            timeoutIdsRef.current.push(timeoutId);
        } else {
            // Multiple lines
            const lines = selectedText.split('\n');
            const indentedLines = lines.map((line) => '  ' + line);
            const newText = beforeSelection + indentedLines.join('\n') + afterSelection;

            onChange(newText);
            const timeoutId = setTimeout(() => {
                const newStart = start + 2;
                const newEnd = end + indentedLines.length * 2;
                textarea.setSelectionRange(newStart, newEnd);
                textarea.focus();
            }, 0);
            timeoutIdsRef.current.push(timeoutId);
        }
    }, [value, onChange, getSelectionInfo]);

    // Outdent: remove 2 spaces from line start (if present), adjust cursor position
    const handleOutdent = useCallback(() => {
        const info = getSelectionInfo();
        if (!info) return;

        const textarea = textareaRef.current;
        if (!textarea) return;

        const { start, end } = info;
        const selectedText = value.substring(start, end);
        const beforeSelection = value.substring(0, start);
        const afterSelection = value.substring(end);

        if (!selectedText.includes('\n')) {
            // Single line
            const lines = value.split('\n');
            let currentPos = 0;
            let lineIndex = 0;

            for (let i = 0; i < lines.length; i++) {
                if (currentPos + lines[i].length >= start) {
                    lineIndex = i;
                    break;
                }
                currentPos += lines[i].length + 1;
            }

            const line = lines[lineIndex];
            if (line.startsWith('  ')) {
                lines[lineIndex] = line.substring(2);
                const newText = lines.join('\n');
                onChange(newText);

                const timeoutId = setTimeout(() => {
                    const newPos = Math.max(start - 2, 0);
                    textarea.setSelectionRange(newPos, newPos);
                    textarea.focus();
                }, 0);
                timeoutIdsRef.current.push(timeoutId);
            }
        } else {
            // Multiple lines
            const lines = selectedText.split('\n');
            const outdentedLines = lines.map((line) => {
                if (line.startsWith('  ')) {
                    return line.substring(2);
                }
                return line;
            });

            const newText = beforeSelection + outdentedLines.join('\n') + afterSelection;
            const removedSpaces = lines.reduce((acc, line, idx) => {
                return acc + (line.startsWith('  ') ? 2 : 0);
            }, 0);

            onChange(newText);
            const timeoutId = setTimeout(() => {
                const newStart = Math.max(start - 2, 0);
                const newEnd = Math.max(end - removedSpaces, newStart);
                textarea.setSelectionRange(newStart, newEnd);
                textarea.focus();
            }, 0);
            timeoutIdsRef.current.push(timeoutId);
        }
    }, [value, onChange, getSelectionInfo]);

    // Handle Tab (indent), Shift+Tab (outdent), Enter (list continuation/exit), preserve cursor
    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLTextAreaElement>) => {
            const textarea = textareaRef.current;
            if (!textarea) return;

            // Handle Tab key for indentation
            if (e.key === 'Tab') {
                e.preventDefault();
                if (e.shiftKey) {
                    handleOutdent();
                } else {
                    handleIndent();
                }
                return;
            }

            // Handle Enter key for list continuation
            if (e.key === 'Enter' && !e.shiftKey) {
                const start = textarea.selectionStart;
                const text = textarea.value;
                const lineStart = text.lastIndexOf('\n', start - 1) + 1;
                const currentLine = text.substring(lineStart, start);

                // Check for bullet list
                const bulletMatch = currentLine.match(/^(\s*)• /);
                if (bulletMatch) {
                    const indent = bulletMatch[1];
                    const lineContent = currentLine.substring(bulletMatch[0].length);

                    if (lineContent.trim() === '') {
                        // Empty bullet - remove it and create normal line
                        e.preventDefault();
                        const before = text.substring(0, lineStart);
                        const after = text.substring(start);
                        onChange(before + '\n' + after);
                        const timeoutId = setTimeout(() => {
                            if (textareaRef.current) {
                                textareaRef.current.focus();
                                textareaRef.current.setSelectionRange(lineStart + 1, lineStart + 1);
                            }
                        }, 0);
                        timeoutIdsRef.current.push(timeoutId);
                    } else {
                        // Continue bullet list
                        e.preventDefault();
                        const before = text.substring(0, start);
                        const after = text.substring(start);
                        const insertion = `\n${indent}• `;
                        const newText = before + insertion + after;
                        onChange(newText);
                        const timeoutId = setTimeout(() => {
                            if (textareaRef.current) {
                                // Position cursor after the inserted newline + indent + bullet + space
                                const newPos = start + insertion.length;
                                textareaRef.current.focus();
                                textareaRef.current.setSelectionRange(newPos, newPos);
                            }
                        }, 0);
                        timeoutIdsRef.current.push(timeoutId);
                    }
                    return;
                }

                // Check for numbered list
                const numberMatch = currentLine.match(/^(\s*)(\d+)\. /);
                if (numberMatch) {
                    const indent = numberMatch[1];
                    const num = numberMatch[2];
                    const lineContent = currentLine.substring(numberMatch[0].length);

                    if (lineContent.trim() === '') {
                        // Empty numbered item - remove it and create normal line
                        e.preventDefault();
                        const before = text.substring(0, lineStart);
                        const after = text.substring(start);
                        onChange(before + '\n' + after);
                        const timeoutId = setTimeout(() => {
                            if (textareaRef.current) {
                                textareaRef.current.focus();
                                textareaRef.current.setSelectionRange(lineStart + 1, lineStart + 1);
                            }
                        }, 0);
                        timeoutIdsRef.current.push(timeoutId);
                    } else {
                        // Continue numbered list
                        e.preventDefault();
                        const nextNum = parseInt(num) + 1;
                        const before = text.substring(0, start);
                        const after = text.substring(start);
                        const insertion = `\n${indent}${nextNum}. `;
                        const newText = before + insertion + after;
                        onChange(newText);
                        const timeoutId = setTimeout(() => {
                            if (textareaRef.current) {
                                // Position cursor after the inserted newline + indent + number + dot + space
                                const newPos = start + insertion.length;
                                textareaRef.current.focus();
                                textareaRef.current.setSelectionRange(newPos, newPos);
                            }
                        }, 0);
                        timeoutIdsRef.current.push(timeoutId);
                    }
                    return;
                }

                // Not a list item - allow default Enter behavior
            }
        },
        [handleIndent, handleOutdent, onChange]
    );

    const handleImprove = async () => {
        setIsImproving(true);
        setIsImproveModalOpen(true);
        try {
            const chatService = ChatService.getInstance();
            const response = await chatService.sendImprovementRequest(value);
            setImprovedText(typeof response === 'string' ? response : response.response);
        } catch (error) {
            console.error('Error improving text:', error);
        } finally {
            setIsImproving(false);
        }
    };

    const handleAcceptImprovement = () => {
        onChange(improvedText);
        setIsImproveModalOpen(false);
    };

    return (
        <Box style={{ width: '100%' }}>
            {label && (
                <Text component="label" size="sm" fw={500} mb={5} display="block">
                    {label}
                    {required && (
                        <Text component="span" c="red" ml={4}>
                            *
                        </Text>
                    )}
                </Text>
            )}

            <Box
                style={{
                    border: `1px solid var(--mantine-color-${isDark ? 'dark-4' : 'gray-4'})`,
                    borderRadius: 'var(--mantine-radius-sm)',
                    overflow: 'hidden',
                }}
            >
                <Box
                    p={6}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        backgroundColor: `var(--mantine-color-${isDark ? 'dark-8' : 'gray-1'})`,
                        borderBottom: `1px solid var(--mantine-color-${isDark ? 'dark-4' : 'gray-4'})`,
                    }}
                >
                    {enableImprovement && (
                        <Button
                            variant="subtle"
                            size="xs"
                            leftSection={<SparklesIcon style={{ width: 16, height: 16 }} />}
                            onClick={handleImprove}
                            onMouseDown={(e) => e.preventDefault()}
                            disabled={disabled || !value?.trim()}
                        >
                            Improve
                        </Button>
                    )}

                    <Box
                        style={{
                            width: '1px',
                            height: '24px',
                            backgroundColor: `var(--mantine-color-${isDark ? 'dark-4' : 'gray-4'})`,
                            margin: '0 4px',
                        }}
                    />

                    <Tooltip label="Bullet list" position="top" withArrow>
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={handleAddBullets}
                            onMouseDown={(e) => e.preventDefault()}
                            disabled={disabled}
                        >
                            <ListBulletIcon style={{ width: 16, height: 16 }} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Numbered list" position="top" withArrow>
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={handleAddNumbering}
                            onMouseDown={(e) => e.preventDefault()}
                            disabled={disabled}
                        >
                            <NumberedListIcon style={{ width: 16, height: 16 }} />
                        </ActionIcon>
                    </Tooltip>

                    <Box
                        style={{
                            width: '1px',
                            height: '24px',
                            backgroundColor: `var(--mantine-color-${isDark ? 'dark-4' : 'gray-4'})`,
                            margin: '0 4px',
                        }}
                    />

                    <Tooltip label="Decrease indent (Shift+Tab)" position="top" withArrow>
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={handleOutdent}
                            onMouseDown={(e) => e.preventDefault()}
                            disabled={disabled}
                        >
                            <ArrowLeftIcon style={{ width: 16, height: 16 }} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Increase indent (Tab)" position="top" withArrow>
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={handleIndent}
                            onMouseDown={(e) => e.preventDefault()}
                            disabled={disabled}
                        >
                            <ArrowRightIcon style={{ width: 16, height: 16 }} />
                        </ActionIcon>
                    </Tooltip>

                    <Box style={{ flex: 1 }} />

                    <Tooltip
                        label="Use toolbar buttons to format text. Press Enter to continue lists, or double-Enter to exit lists. Use Tab/Shift+Tab for indentation."
                        position="top"
                        multiline
                        maw={300}
                        withArrow
                    >
                        <ActionIcon variant="subtle" color="gray" size="sm">
                            <InformationCircleIcon style={{ width: 16, height: 16 }} />
                        </ActionIcon>
                    </Tooltip>
                </Box>

                <Textarea
                    ref={textareaRef}
                    value={value}
                    onChange={(e) => onChange(e.currentTarget.value)}
                    onPaste={handlePaste}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    autosize
                    minRows={minRows}
                    maxRows={maxRows}
                    disabled={disabled}
                    error={error}
                    {...textareaProps}
                    styles={{
                        input: {
                            resize: 'vertical' as const,
                            border: 'none',
                            borderRadius: 0,
                        },
                        wrapper: {
                            marginBottom: 0,
                        },
                    }}
                />
            </Box>

            {error && (
                <Text size="xs" c="red" mt={5}>
                    {error}
                </Text>
            )}

            {enableImprovement && (
                <Modal
                    opened={isImproveModalOpen}
                    onClose={() => setIsImproveModalOpen(false)}
                    title="Improved Text"
                    size="lg"
                >
                    <div style={{ position: 'relative', minHeight: '100px' }}>
                        <LoadingOverlay visible={isImproving} />
                        <Text size="sm" mb="lg">{improvedText}</Text>

                        <Group justify="flex-end" mt="xl">
                            <Button variant="subtle" color="gray" onClick={() => setIsImproveModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button variant="light" color="blue" onClick={handleImprove}>
                                Retry
                            </Button>
                            <Button onClick={handleAcceptImprovement}>
                                Accept
                            </Button>
                        </Group>
                    </div>
                </Modal>
            )}
        </Box>
    );
}
